/**
 * ENHANCED SKY, the arithmetic half: which `BARON.PAL` entries the hemisphere
 * is built out of, how much light the sun's bearing adds, and what the
 * mission's **sky word** does to all of it. No three.js here, so every number
 * below is a plain unit test (`enhanced-sky-look.test.ts`) — the same split
 * `effect-sprites.ts` uses.
 *
 * This is a PORT EXTENSION in whole. `time-of-day.ts` is the authentic layer
 * and stays untouched: its flat measured noon sky, its `sun.c` 15-degrees-an-
 * hour law and its `SUN.BMP` discs are what `/fly` draws with this toggle off.
 * What is extended here is only the *painting*, and the constraint it is
 * painted under is that **every colour is a palette entry**, quoted by index.
 *
 * ## The gradient, and why it is three stops rather than two
 *
 * `time-of-day.ts` walks one run from zenith to horizon, which is what
 * `setSkyShades(lo, hi, ...)` does. A real sky is two runs meeting: a deep
 * band overhead, the everyday blue through the middle, haze at the skyline.
 * `BARON.PAL` happens to carry both — the **100-111** ramp is a deep blue
 * (`#083851` -> `#206182`) sitting right below the **48-63** sky ramp
 * (`#7db6c3` -> `#b6b6b6`) — so a three-stop dome needs nothing invented.
 *
 * Both blend points are stated as an **elevation in degrees**, never as an
 * exponent. That is `lessons.md`'s 2026-08-22 rule ("most of the sphere and
 * most of the screen are different quantities"), and the daytime haze band
 * keeps the *measured* 3.4 degrees `DAYTIME_HAZE_CONCENTRATION` was calibrated
 * to, so a level daytime view still reads as the `#7db6c3` the original was
 * measured at.
 *
 * ## The horizon has two colours, picked by bearing
 *
 * `setSkyShades` bands the sky in one dimension, elevation. The dawn and dusk
 * skies people remember are banded in two: the skyline is orange *where the
 * sun is* and stays cold blue behind you. So `horizonWarm` and `horizonCool`
 * are mixed by how close a point's compass bearing is to the sun's — the same
 * run, resolved over the sphere instead of over a scanline.
 *
 * ## The sky word finally means something
 *
 * `mission-world.ts`'s four `PS.EXE` sky words were briefing text and nothing
 * else. Here `clear`/`partly cloudy`/`cloudy`/`overcast` pick a grey to wash
 * the dome toward, how much of the sun's glare survives, how dark the ground
 * goes, and (in `sky-clouds.ts`) what is actually in the air. The greys come
 * from `BARON.PAL`'s own 64-87 neutral ramp, one per time of day, because
 * mixing a night sky toward a daytime grey would make an overcast midnight
 * *brighter* than a clear one.
 *
 * That per-hour table now lives in `overcast-sky.ts` as `OVERCAST_DECK_GREY`,
 * because the flat deck `sun.c`'s own repaint paints below 9,375 ft had the
 * same hour problem and needs the same answer. One table, so the deck and the
 * wash cannot drift apart. Its daytime entry is the binary's 84 `#bababa`
 * rather than the 83 `#b2b2b2` this module used to hold privately — one step of
 * a 24-entry ramp, and under a VGA DAC step once the largest surviving
 * `greyMix` (0.22, `cloudy`) has scaled it.
 */

import type { SkyCondition, TimeOfDay } from "@red-opus/engine";
import { DAYTIME_HAZE_CONCENTRATION, type PaletteColour } from "./time-of-day.ts";
import { OVERCAST_DECK_GREY } from "./overcast-sky.ts";

/**
 * The palette entries one hemisphere is made of.
 *
 * `glareCore` and `glareWash` are *radiance*, not paint: they are added on top
 * of the gradient in HDR and may take the result well past display white, which
 * is the whole point of `enhanced-sky.ts`'s tone curve. Both come out of
 * `BARON.PAL` 209-216, the palette's own white-hot-to-red fire ramp — the
 * nearest thing the original has to a statement about what bright looks like.
 */
export interface EnhancedSkyStops {
  /** Straight up. `BARON.PAL` 100-111's deep blue by day, 0 at night. */
  readonly zenith: PaletteColour;
  /** The colour a level view is mostly filled with. Daytime's is the measured `#7db6c3`. */
  readonly mid: PaletteColour;
  /** The skyline away from the sun's bearing. */
  readonly horizonCool: PaletteColour;
  /** The skyline in the sun's bearing. Equal to `horizonCool` where the hour has no warmth in it. */
  readonly horizonWarm: PaletteColour;
  /** The tight halo just outside the disc. */
  readonly glareCore: PaletteColour;
  /** The broad wash around it. */
  readonly glareWash: PaletteColour;
  /** Where the mid colour has blended half-way into the zenith, in degrees of elevation. */
  readonly midElevationDeg: number;
  /** Where the mid colour has blended half-way into the horizon, in degrees of elevation. */
  readonly hazeElevationDeg: number;
  /** Half-angle in degrees at which the warm horizon has faded half-way back to the cool one. */
  readonly warmthSpreadDeg: number;
  /** Peak radiance of the tight halo, in units of display white. */
  readonly glareCoreGain: number;
  /** Peak radiance of the broad wash, in units of display white. */
  readonly glareWashGain: number;
}

/**
 * Half-angle of the tight halo, in degrees. Inside the disc, which
 * `sky-dressing.ts` measures at 4.1 degrees across (2.05 of radius) at this
 * port's field of view — so the blown-out part of the core stays *within the
 * sun's own silhouette* and what escapes it is already a gradient.
 *
 * The first captured pass had this at 4.5 with a gain of 4, and the noon sun
 * came out as a **17-degree white ball** (`enhanced-sky.md` §6). Restrained
 * means the disc keeps its size; only its edge glows.
 */
export const GLARE_CORE_HALF_ANGLE_DEG = 3.5;
/** Half-angle of the broad wash. Wide enough to be a wash, tight enough not to be a second sky. */
export const GLARE_WASH_HALF_ANGLE_DEG = 19;
/** Azimuthal half-angle of the low-sun streak — wide across, and `STREAK_ELEVATION_FALLOFF` tight up. */
export const STREAK_HALF_ANGLE_DEG = 42;
/** How fast the streak dies with vertical separation from the sun, per unit of `sin(elevation)`. */
export const STREAK_ELEVATION_FALLOFF = 26;
/** Above this solar elevation there is no streak at all: it is a low-sun effect and nothing else. */
export const STREAK_MAX_SUN_ELEVATION_DEG = 20;
/** Peak radiance of the streak at a sun on the horizon, in units of display white. */
export const STREAK_GAIN = 0.9;

/**
 * Above this solar elevation the horizon has no warm side at all.
 *
 * Not a taste setting — a correctness one, and it was found in a capture. The
 * warm/cool horizon mix is chosen by *compass bearing to the sun*, and a sun
 * near the zenith has no meaningful bearing: its horizontal component goes to
 * zero and the normalisation that recovers a bearing from it returns whichever
 * way the floor divisor points. The first noon capture of this dome
 * (`enhanced-sky.md` §6) accordingly grew a broad orange band along one side of
 * the skyline at **midday**, which is exactly the tell. Fading the whole term
 * out as the sun climbs removes the artefact and states the physical claim at
 * the same time: a sky is warm at the horizon *because* the sun is low.
 */
export const HORIZON_WARMTH_MAX_SUN_ELEVATION_DEG = 35;

/**
 * How much of the warm-horizon mix a sun at this elevation earns. `sinElevation`
 * is the sun direction's `y`; below the horizon there is no sun to warm
 * anything, which is `sun.c`'s own `sin(angle) < 0` test again.
 */
export function horizonWarmthForSun(sinElevation: number): number {
  const ceiling = Math.sin((HORIZON_WARMTH_MAX_SUN_ELEVATION_DEG * Math.PI) / 180);
  if (sinElevation <= 0 || sinElevation >= ceiling) return 0;
  return 1 - sinElevation / ceiling;
}

const DAYTIME_STOPS: EnhancedSkyStops = {
  zenith: 0x206182,
  mid: 0x7db6c3,
  horizonCool: 0xb6b6b6,
  horizonWarm: 0xe7cfc7,
  glareCore: 0xffffe7,
  glareWash: 0xffffa2,
  midElevationDeg: 30,
  hazeElevationDeg: DAYTIME_HAZE_HALF_BAND_DEG(),
  warmthSpreadDeg: 45,
  glareCoreGain: 2,
  glareWashGain: 0.35,
};

/**
 * The elevation `time-of-day.ts`'s calibrated daytime concentration puts its
 * half-way band at, recomputed from that constant rather than restated as
 * 3.4 — if the calibration ever moves, the enhanced haze band moves with it.
 */
function DAYTIME_HAZE_HALF_BAND_DEG(): number {
  return (Math.asin(1 - 0.5 ** (1 / DAYTIME_HAZE_CONCENTRATION)) * 180) / Math.PI;
}

/**
 * ## Why twilight's cool horizon is on the *sunset* ramp
 *
 * The first version put `BARON.PAL` 5 `#34516d` (dawn) and 3 `#284559` (dusk)
 * there — the twilight-blue run, on the reasoning that the sky behind you at
 * sunset is blue. Measured against the `timeOfDay` sky it replaces, that made
 * ENHANCED a **downgrade** at both twilight hours: `setSkyShades`' run paints
 * one horizon colour at every bearing, so the banded dusk sky is orange all
 * round, while a bearing-mixed one collapses to navy the moment you are not
 * looking at the sun — which is most of a sortie. A same-view A/B at dusk had
 * the enhanced sky's mean falling from rgb(148,102,70) to rgb(52,74,91): a
 * change of 49/255 per channel, in the wrong direction.
 *
 * Both cool stops are now **the sunset ramp's own dim end** — 247 `#ba5d14` at
 * dawn under 254 `#ffaa5d`, 245 `#a64904` at dusk under 250 `#d77930`. So the
 * whole skyline stays a sunset and the bearing gradient still says which way
 * the sun is, as a walk *down one ramp* rather than a jump to another: exactly
 * what `ramp-shade.ts` does to a face, applied to the horizon. The blues have
 * not gone anywhere — they are `zenith` and `mid`, which is where a twilight
 * sky is actually blue.
 */
const DAWN_STOPS: EnhancedSkyStops = {
  zenith: 0x104965,
  mid: 0x416582,
  horizonCool: 0xba5d14,
  horizonWarm: 0xffaa5d,
  glareCore: 0xffffe7,
  glareWash: 0xf39e51,
  midElevationDeg: 35,
  hazeElevationDeg: 12,
  warmthSpreadDeg: 70,
  glareCoreGain: 2.2,
  glareWashGain: 0.8,
};

const DUSK_STOPS: EnhancedSkyStops = {
  zenith: 0x0c3c59,
  mid: 0x304965,
  horizonCool: 0xa64904,
  horizonWarm: 0xd77930,
  glareCore: 0xffffe7,
  glareWash: 0xcf6d24,
  midElevationDeg: 35,
  hazeElevationDeg: 12,
  warmthSpreadDeg: 70,
  glareCoreGain: 2,
  glareWashGain: 0.9,
};

/**
 * Night's "glare" is the moon's, and it is a halo rather than a glare: gains
 * two orders below the sun's, `BARON.PAL` 199 (the moon's own disc colour) at
 * the core and 195 (the brightest star grey) in the wash.
 */
const NIGHT_STOPS: EnhancedSkyStops = {
  zenith: 0x000000,
  mid: 0x203449,
  horizonCool: 0x243c51,
  horizonWarm: 0x243c51,
  glareCore: 0xcbcbcb,
  glareWash: 0x71aac3,
  midElevationDeg: 40,
  hazeElevationDeg: 12,
  warmthSpreadDeg: 45,
  glareCoreGain: 0.35,
  glareWashGain: 0.06,
};

/** Exported so `ocean-look.ts` can take the hour's *glare* pair without inventing a ground colour to satisfy `enhancedSkyLookFor` — the sea's glitter and the sky's glare have to be the same two `BARON.PAL` entries. */
export const STOPS_BY_HOUR: Readonly<Record<TimeOfDay, EnhancedSkyStops>> = {
  daytime: DAYTIME_STOPS,
  dawn: DAWN_STOPS,
  dusk: DUSK_STOPS,
  night: NIGHT_STOPS,
};

/** What a sky word does to the dome, the light and the ground. `sky-clouds.ts` reads the cover half. */
export interface SkyWordTreatment {
  /** How far the whole dome is mixed toward `overcastGreyFor`, in `[0, 1]`. */
  readonly greyMix: number;
  /** What fraction of the sun's glare radiance survives the cloud in front of it. */
  readonly glareScale: number;
  /**
   * The darkest ground this word allows. `enhancedSkyLookFor` takes whichever
   * of this and the hour's own ground is darker, so an overcast midnight is not
   * brighter than a clear one.
   *
   * **Every one is an entry of `world/terrain-palette.ts`'s
   * `GROUND_RELIEF_RAMP`**, and that is a constraint rather than a coincidence.
   * The relief mesh dims by walking that run (`time-of-day.ts`'s
   * `groundRampShiftForColour`), so a floor picked from anywhere else would be
   * a colour the terrain cannot reach: the flat backdrop plane would darken
   * under an overcast sky and the hills in front of it would not. Two of these
   * originally were off the ramp — 97 and 95, which sit in the palette's own
   * gaps — and moving them to 98 and 96 costs nothing visible and keeps the two
   * halves of the ground on one walk.
   */
  readonly groundFloor: PaletteColour;
}

/**
 * The four `PS.EXE` sky words, as picture. Greys, glare survival and ground
 * floors are this port's choice; the words and their order are the game's.
 *
 * The floors are the ramp's centre and its next three steps down — 143, 99, 98,
 * 96 — which are also, exactly, the entries the four *hours* already walk to.
 */
export const SKY_WORD_TREATMENTS: Readonly<Record<SkyCondition, SkyWordTreatment>> = {
  clear: { greyMix: 0, glareScale: 1, groundFloor: 0x4d5928 },
  "partly cloudy": { greyMix: 0.06, glareScale: 0.95, groundFloor: 0x2c4520 },
  cloudy: { greyMix: 0.22, glareScale: 0.7, groundFloor: 0x283c1c },
  overcast: { greyMix: 0.55, glareScale: 0.15, groundFloor: 0x243414 },
};

/**
 * Blends two palette entries per channel. The result is **not** a palette
 * entry, which is the one place this module leaves the ramp: an overcast dome
 * sits between the hour's colour and the hour's grey, and `BARON.PAL` has no
 * entry there. `three/ordered-dither.ts` puts it back on the 6-bit DAC grid the
 * palette itself lives on, so the framebuffer still only ever holds values the
 * hardware of the day could address.
 */
export function mixPalette(from: PaletteColour, to: PaletteColour, amount: number): PaletteColour {
  const t = Math.min(Math.max(amount, 0), 1);
  const channel = (shift: number): number =>
    Math.round(((from >> shift) & 0xff) * (1 - t) + ((to >> shift) & 0xff) * t) & 0xff;
  return (channel(16) << 16) | (channel(8) << 8) | channel(0);
}

/**
 * The look's four gradient stops after its sky word has washed them toward the
 * hour's overcast grey. The glare colours are deliberately **not** washed — the
 * word scales glare with `glareScale` instead, because cloud in front of the
 * sun takes light away rather than turning it grey.
 */
export function greyedStops(look: EnhancedSkyLook): EnhancedSkyStops {
  const { stops, overcastGrey, treatment } = look;
  if (treatment.greyMix <= 0) return stops;
  return {
    ...stops,
    zenith: mixPalette(stops.zenith, overcastGrey, treatment.greyMix),
    mid: mixPalette(stops.mid, overcastGrey, treatment.greyMix),
    horizonCool: mixPalette(stops.horizonCool, overcastGrey, treatment.greyMix),
    horizonWarm: mixPalette(stops.horizonWarm, overcastGrey, treatment.greyMix),
  };
}

/** Rec. 601 luma, the same weighting `palette-index.ts` sorts a ramp by. */
export function paletteLuma(colour: PaletteColour): number {
  return 0.299 * ((colour >> 16) & 0xff) + 0.587 * ((colour >> 8) & 0xff) + 0.114 * (colour & 0xff);
}

/** The darker of two palette entries — never a blend, so the result is still an entry. */
export function darkerOf(a: PaletteColour, b: PaletteColour): PaletteColour {
  return paletteLuma(a) <= paletteLuma(b) ? a : b;
}

/** Everything one mission's sky is drawn from. */
export interface EnhancedSkyLook {
  readonly stops: EnhancedSkyStops;
  readonly overcastGrey: PaletteColour;
  readonly treatment: SkyWordTreatment;
  readonly ground: PaletteColour;
  readonly timeOfDay: TimeOfDay;
  readonly sky: SkyCondition;
}

/** A dogfight or a duel briefs no weather at all; it gets the enhanced noon sky under a clear word. */
export const DEFAULT_ENHANCED_HOUR: TimeOfDay = "daytime";
export const DEFAULT_ENHANCED_SKY_WORD: SkyCondition = "clear";

/**
 * `groundColour` is the hour's own ground from `time-of-day.ts` — passed in
 * rather than re-derived, so the two layers can never disagree about what
 * colour the ground is at a given hour.
 */
export function enhancedSkyLookFor(
  timeOfDay: TimeOfDay | undefined,
  sky: SkyCondition | undefined,
  groundColour: PaletteColour,
): EnhancedSkyLook {
  const hour = timeOfDay ?? DEFAULT_ENHANCED_HOUR;
  const word = sky ?? DEFAULT_ENHANCED_SKY_WORD;
  const treatment = SKY_WORD_TREATMENTS[word];
  return {
    stops: STOPS_BY_HOUR[hour],
    overcastGrey: OVERCAST_DECK_GREY[hour],
    treatment,
    ground: darkerOf(groundColour, treatment.groundFloor),
    timeOfDay: hour,
    sky: word,
  };
}

/**
 * The exponent that puts a `pow(x, e)` blend's half-way point at a given
 * elevation, where `x` is `sin(elevation)` for the zenith blend and
 * `1 - sin(elevation)` for the haze one.
 *
 * Stated this way round because an exponent has no wrong-looking value and an
 * angle does (`lessons.md`, 2026-08-22). A degenerate angle — 0 or 90 — would
 * divide by `log(1)`; both are clamped away from the ends rather than guarded
 * at the call site, since a blend at exactly the horizon is not a thing anyone
 * means.
 */
export function blendExponentForElevation(elevationDeg: number, towardsHorizon: boolean): number {
  const clamped = Math.min(Math.max(elevationDeg, 0.05), 89.95);
  const sine = Math.sin((clamped * Math.PI) / 180);
  return Math.log(0.5) / Math.log(towardsHorizon ? 1 - sine : sine);
}

/**
 * The exponent that puts a `pow(cos(angle), e)` lobe's half-power point at a
 * given half-angle — how `GLARE_CORE_HALF_ANGLE_DEG` and its siblings become
 * shader constants.
 */
export function lobeExponentForHalfAngle(halfAngleDeg: number): number {
  const cosine = Math.cos((Math.min(Math.max(halfAngleDeg, 0.05), 89.95) * Math.PI) / 180);
  return Math.log(0.5) / Math.log(cosine);
}

/**
 * How much of `STREAK_GAIN` a sun at this elevation still earns: full on the
 * horizon, nothing at `STREAK_MAX_SUN_ELEVATION_DEG` and above. `sinElevation`
 * is the sun direction's `y`, which is exactly what `sun.c`'s own day/night
 * test reads.
 */
export function streakGainForSun(sinElevation: number): number {
  const ceiling = Math.sin((STREAK_MAX_SUN_ELEVATION_DEG * Math.PI) / 180);
  if (sinElevation <= 0 || sinElevation >= ceiling) return 0;
  return STREAK_GAIN * (1 - sinElevation / ceiling);
}
